import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { Tables } from '@/integrations/supabase/types';

export type Product = Tables<'products'>;

export function useProducts() {
  return useQuery({
    queryKey: ['products'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('products')
        .select('id,name,slug,logo_url,description,category,pricing_monthly,verified_pricing,verification_status,trust_score,verified_customer_count,avg_retention_months,listing_tier,marketing_gap,alternative_to,featured,website_url,founder_name,founder_avatar,created_at,updated_at,owner_id,stripe_connected,verified_mrr,monthly_churn_pct,mrr_growth_pct,last_verified_at,tier_expires_at,seo_title,seo_description,analysis,case_study_url,implementation_cost,avg_time_saved_hrs_per_month,avg_value_lift_pct')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data as Product[];
    },
    staleTime: 60_000,
    gcTime: 300_000,
  });
}

export function useProduct(slug: string | undefined) {
  return useQuery({
    queryKey: ['products', slug],
    queryFn: async () => {
      if (!slug) return null;
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('slug', slug)
        .single();
      if (error) throw error;
      return data as Product;
    },
    enabled: !!slug,
    staleTime: 60_000,
    gcTime: 300_000,
  });
}

export function useUpdateProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<Product> }) => {
      const { data, error } = await supabase
        .from('products')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });
}

export function useDeleteProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('products').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });
}
